import classnames from 'classnames';
import './thumbnailBeat.styles.scss';
import { ReviewedIcon as reviewMarkIcon } from '@/shared/ui';
import useThumbnailBeat from './useThumbnailBeat';

export type ThumbnailBeatProps = {
  id?: string;
  type?: string;
  onClickSelect?: () => void;
  // Optional: the useEffect below only calls drawStrip when ecgData?.length > 0,
  // and width/height/beatMarkers/hesBeatStatus are only read inside that call.
  ecgData?: number[][];
  beatMarkers?: number[];
  hesBeatStatus?: number[];
  channels?: number[];
  gain?: number;
  speed?: number;
  width?: number;
  height?: number;
  isSelected?: boolean;
  isActive?: boolean;
  isChanged?: boolean;
  disabled?: boolean;
  className?: string;
  highPass?: number;
  isReviewed?: boolean;
  morphologyName?: string;
};

/**
 * Renders one beat morphology thumbnail as a clickable SVG tile. Takes its drawing surface ref from useThumbnailBeat, composes the active, changed and selected modifier classes, and overlays the channel label, the morphology name, a Deleted marker for deleted beats and the review tick in the tile's corners.
 *
 * @param props - Identity, geometry, channel and state flags for the beat tile.
 * @returns The thumbnail button wrapping the beat's SVG canvas and its corner labels.
 */
const ThumbnailBeat = ({
  id,
  type,
  onClickSelect,
  ecgData,
  beatMarkers,
  hesBeatStatus,
  channels = [2],
  gain,
  speed,
  width,
  height,
  isSelected,
  isActive,
  isChanged,
  disabled = false,
  className,
  highPass,
  isReviewed,
  morphologyName,
}: ThumbnailBeatProps) => {
  const { canvasRef } = useThumbnailBeat({
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels,
    gain,
    speed,
    width,
    height,
    highPass,
  });

  return (
    <button
      id={id}
      disabled={disabled}
      className={classnames(
        'single-strip-beat',
        className,
        isActive ? '--active' : '',
        isChanged ? '--changed' : '',
        isSelected ? '--selected' : '',
      )}
      onClick={onClickSelect}
    >
      <svg ref={canvasRef} width={width} height={height} />
      <div className="top-left-corner" />
      <div className="bottom-left-corner">
        {channels?.length === 1 ? <div className="channel-text">{`CH${channels[0]}`}</div> : null}
        {morphologyName && (
          <div className="channel-text morphology-text">{`${channels?.length !== 1 ? '' : ' - '}${morphologyName}`}</div>
        )}
      </div>
      <div className="bottom-right-corner">
        {type && type === 'Deleted' && <div className="strip-right">{type}</div>}
      </div>
      <div className="top-right-corner">
        {isReviewed && <img src={reviewMarkIcon} alt="review thumbnail" />}
      </div>
    </button>
  );
};

export default ThumbnailBeat;
